import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommunityCard from '../../components/communities/CommunityCard/CommunityCard';
import CommunityEventCard from '../../components/events/CommunityEventCard/CommunityEventCard';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import { Tabs, Tab } from '../../components/ui/Tabs';
import { useChannels } from '../../hooks/useChannels';
import { useEvents } from '../../hooks/useEvents';
import { useGeneral } from '../../contexts';
import Services from '../../services/Services';
import { EventListItem, ChannelListItem } from '../../types';
import { generateEventUrl, generateCommunityUrl } from '../../utils/urlSlug';
import SEO from '../../components/SEO';
import transitionStyles from '../../styles/transitions.module.css';
import styles from './CommunitiesListPage.module.css';

const CommunitiesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token, user } = auth;
  const { getTranslation } = language;
  const userId = user?.id || '';

  const [activeTab, setActiveTab] = useState<'all' | 'joined'>('all');

  // Pagination for all channels
  const [start, setStart] = useState(0);
  const LIMIT = 10;
  const [allChannelsList, setAllChannelsList] = useState<ChannelListItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  // Fetch all communities (paginated)
  const { channels: fetchedChannels, loading: loadingAllChannels } = useChannels({
    token: token || '',
    all: true,
    start,
    limit: LIMIT
  });

  // Update allChannelsList when fetchedChannels changes
  useEffect(() => {
    if (fetchedChannels && fetchedChannels.length > 0) {
      setAllChannelsList(prev => {
        if (start === 0) {
             return fetchedChannels as ChannelListItem[];
        }
        const newItems = (fetchedChannels as ChannelListItem[]).filter(
          ch => !prev.some(p => p.channelID === ch.channelID)
        );
        return [...prev, ...newItems];
      });
      
      if (fetchedChannels.length < LIMIT) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } else if (fetchedChannels && fetchedChannels.length === 0) {
        setHasMore(false);
    }
  }, [fetchedChannels, start]);

  const handleLoadMore = () => {
    setStart(prev => prev + LIMIT);
  };

  // Fetch user's joined communities
  const { channels: joinedChannels, loading: loadingJoinedChannels, refresh: refreshJoinedChannels } = useChannels({
    token: token || '',
    userID: userId,
    popular: false,
    loadingKey: 'communities.joinedChannels'
  });

  // Fetch events
  const { events: allEvents, loading: loadingEvents } = useEvents({
    token: token || '',
    loadingKey: 'communities.events',
    useClosestDates: true
  });

  // Local optimistic state for joined channel IDs
  const [localJoinedIds, setLocalJoinedIds] = useState<Set<string>>(new Set());

  // Sync from hook data
  useEffect(() => {
    setLocalJoinedIds(new Set(joinedChannels.map(ch => ch.channelID)));
  }, [joinedChannels]);

  // Create a Set of joined channel IDs for quick lookup
  const joinedChannelIds = localJoinedIds;

  // Get channels to display based on active tab
  const displayedChannels = useMemo(() => {
    if (activeTab === 'joined') {
      return joinedChannels;
    }
    return allChannelsList;
  }, [activeTab, allChannelsList, joinedChannels]);

  // Filter events based on private channel access
  const filteredEvents = useMemo(() => {
    let filtered = allEvents;
    
    // Filter out unlisted events (isPublic = "3")
    filtered = filtered.filter((event) => event.isPublic !== "3");

    // Filter out events from private channels where user is not a member
    filtered = filtered.filter((event) => {
      if (event.channelDetails) {
        const isPublic = event.channelDetails.isPublic === "1";
        const channelID = event.channelDetails.channelID || event.channelID;

        if (isPublic) {
          return true;
        }

        return joinedChannelIds.has(String(channelID));
      }
      return true;
    });

    return filtered;
  }, [allEvents, joinedChannelIds]);

  const handleCommunityClick = useCallback((community: ChannelListItem) => {
    const communityTitle = community.channelTitle || community.title;
    navigate(generateCommunityUrl(communityTitle, community.channelID));
  }, [navigate]);

  const handleEventClick = useCallback((event: EventListItem) => {
    navigate(generateEventUrl(event.eventTitle || event.title, event.eventID));
  }, [navigate]);

  const handleJoinCommunity = async (channelID: string) => {
    if (!token || !userId) {
      toastError(getTranslation('communities.joinError'));
      return;
    }

    try {
      const response = await Services.SocialHiveJoinToChannel(token, userId, channelID);
      if (response.data.status) {
        setLocalJoinedIds(prev => { const next = new Set(prev); next.add(channelID); return next; });
        toastSuccess(getTranslation('communities.joinSuccess'));
        refreshJoinedChannels();
      } else {
        toastError(response.data.message || getTranslation('communities.joinError'));
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('communities.joinError'));
    }
  };

  const handleLeaveCommunity = async (channelID: string) => {
    if (!token || !userId) {
      toastError(getTranslation('communities.leaveError'));
      return;
    }

    try {
      const response = await Services.SocialHiveLeaveChannel(token, userId, channelID);
      if (response.data.status) {
        setLocalJoinedIds(prev => {
          const next = new Set(prev);
          next.delete(channelID);
          return next;
        });
        toastSuccess(getTranslation('communities.leaveSuccess'));
        refreshJoinedChannels();
      } else {
        toastError(response.data.message || getTranslation('communities.leaveError'));
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('communities.leaveError'));
    }
  };

  const loading = activeTab === 'all' ? (start === 0 && loadingAllChannels) : loadingJoinedChannels;

  return (
    <>
      <SEO
        title={getTranslation('navigation.communities') || 'Communities'}
        description="Discover communities on ToGather."
        url={typeof window !== 'undefined' ? `${window.location.origin}/communities` : undefined}
        lang={language.language}
      />
      <div className={styles.communitiesListPage}>
        {/* Tabs - Mobile: Top, Desktop: Sidebar */}
        <div className={styles.mobileTabsContainer}>
          <Tabs variant="pill">
            <Tab
              variant="pill"
              isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            >
              {getTranslation('communities.allCommunities')}
            </Tab>
            <Tab
              variant="pill"
              isActive={activeTab === 'joined'}
              onClick={() => setActiveTab('joined')}
            >
              {getTranslation('communities.joinedCommunities')}
            </Tab>
          </Tabs>
        </div>

      <div className={styles.mainContent}>
        {/* Community Cards Grid */}
        <div className={styles.communitiesGrid}>
          {loading ? (
            <div className={styles.loadingContainer}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <div key={i} className={styles.communityCardSkeleton} aria-hidden="true">
                  <div className={styles.communityCardSkeletonCount}>
                    <LoadingSkeleton width={44} height={20} className={styles.skeletonRounded8} />
                  </div>

                  <div className={styles.communityCardSkeletonTop}>
                    <LoadingSkeleton width={48} height={48} className={styles.skeletonRounded8} />
                  </div>

                  <div className={styles.communityCardSkeletonBody}>
                    <LoadingSkeleton width="70%" height={16} className={styles.skeletonRounded4} />
                    <LoadingSkeleton width="100%" height={14} className={styles.skeletonRounded4} />
                    <LoadingSkeleton width="85%" height={14} className={styles.skeletonRounded4} />
                  </div>

                  <div className={styles.communityCardSkeletonFooter}>
                    <LoadingSkeleton width={110} height={32} className={styles.skeletonPill} />
                  </div>
                </div>
              ))}
            </div>
          ) : displayedChannels.length > 0 ? (
            <>
              {displayedChannels.map((community, index) => (
                <div key={community.channelID} className={transitionStyles.staggerItem} style={{ animationDelay: `${Math.min(index * 50, 300)}ms` }}>
                  <CommunityCard
                    community={community}
                    isJoined={joinedChannelIds.has(community.channelID)}
                    onJoin={handleJoinCommunity}
                    onLeave={handleLeaveCommunity}
                    onClick={handleCommunityClick}
                    showParticipantCount={activeTab === 'all'}
                  />
                </div>
              ))}
              {activeTab === 'all' && hasMore && (
                <div className={styles.loadMoreContainer}>
                  {loadingAllChannels ? (
                    <div className={styles.spinner}></div>
                  ) : (
                    <button 
                      onClick={handleLoadMore} 
                      className={styles.loadMoreButton}
                    >
                      {getTranslation('common.loadMore') || 'Load More'}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <EmptyState title={getTranslation('communities.noCommunities')} />
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className={styles.sidebar}>
        {/* Tabs - Desktop only */}
        <div className={styles.desktopTabsContainer}>
          <Tabs variant="pill">
            <Tab
              variant="pill"
              isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            >
              {getTranslation('communities.allCommunities')}
            </Tab>
            <Tab
              variant="pill"
              isActive={activeTab === 'joined'}
              onClick={() => setActiveTab('joined')}
            >
              {getTranslation('communities.joinedCommunities')}
            </Tab>
          </Tabs>
        </div>

        {/* Events List */}
        <div className={styles.sidebarContent}>
          <div className={styles.eventsSection}>
            <h2 className={styles.eventsSectionTitle}>{getTranslation('communities.events')}</h2>
            {loadingEvents ? (
              <div className={styles.eventsLoadingContainer}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <LoadingSkeleton key={i} width="100%" height={150} />
                ))}
              </div>
            ) : filteredEvents.length > 0 ? (
              <div className={styles.eventsListContainer}>
                {filteredEvents.slice(0, 3).map((event, index) => (
                  <CommunityEventCard
                    key={event.eventID}
                    event={event}
                    onClick={handleEventClick}
                    isLast={index === Math.min(filteredEvents.length, 3) - 1}
                    compactMode={true}
                    showParticipantCount={true}
                  />
                ))}
              </div>
            ) : (
              <EmptyState title={getTranslation('events.eventNotFound')} />
            )}
          </div>
        </div>
      </div>

      {/* Events Section - Mobile only */}
      <div className={styles.mobileEventsSection}>
        <div className={styles.eventsSection}>
          <h2 className={styles.eventsSectionTitle}>{getTranslation('communities.events')}</h2>
          {loadingEvents ? (
            <div className={styles.eventsLoadingContainer}>
              {[1, 2, 3, 4, 5].map((i) => (
                <LoadingSkeleton key={i} width="100%" height={150} />
              ))}
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className={styles.eventsListContainer}>
              {filteredEvents.slice(0, 3).map((event, index) => (
                <CommunityEventCard
                  key={event.eventID}
                  event={event}
                  onClick={handleEventClick}
                  isLast={index === Math.min(filteredEvents.length, 3) - 1}
                />
              ))}
            </div>
          ) : (
            <EmptyState title={getTranslation('events.eventNotFound')} />
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default CommunitiesListPage;

